import React, { useState } from 'react';
import PropTypes from 'prop-types';

/**
 * PracticeSettingsModal - Settings dialog for practice quiz generation
 * 
 * Allows student to configure:
 * - Question count (5, 10, 20, 50, or all)
 * - Focus on weak questions (adaptive mode)
 * - Include new questions
 * 
 * @param {Object} props
 * @param {boolean} props.isOpen - Modal open state
 * @param {Function} props.onClose - Close handler
 * @param {Function} props.onStart - Start practice quiz handler
 * @param {string} props.courseName - Course name
 * @param {number} props.availableQuestions - Total available questions
 */
function PracticeSettingsModal({
  isOpen,
  onClose,
  onStart,
  courseName,
  availableQuestions = 0
}) {
  const [questionCount, setQuestionCount] = useState(10);
  const [focusWeakQuestions, setFocusWeakQuestions] = useState(false);
  const [includeNewQuestions, setIncludeNewQuestions] = useState(true);
  
  if (!isOpen) return null;
  
  const handleStart = () => {
    onStart({
      questionCount: questionCount === 'all' ? availableQuestions : questionCount,
      focusWeakQuestions,
      includeNewQuestions
    });
  };
  
  const questionOptions = [
    { value: 5, label: '5 Fragen' },
    { value: 10, label: '10 Fragen' },
    { value: 20, label: '20 Fragen' },
    { value: 50, label: '50 Fragen' },
    { value: 'all', label: `Alle (${availableQuestions} verfügbar)` }
  ];
  
  return (
    <div 
      className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div 
        className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-md theme-transition"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">
              Übung starten
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 theme-transition"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            {courseName}
          </p>
        </div>
        
        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Question Count Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
              Anzahl Fragen
            </label>
            <div className="grid grid-cols-2 gap-3">
              {questionOptions.map((option) => {
                const isDisabled = option.value !== 'all' && option.value > availableQuestions;
                return (
                  <button
                    key={option.value}
                    onClick={() => !isDisabled && setQuestionCount(option.value)}
                    disabled={isDisabled}
                    className={`
                      p-3 rounded-lg border-2 text-sm font-medium theme-transition
                      ${questionCount === option.value
                        ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                        : isDisabled
                          ? 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-400 dark:text-gray-600 cursor-not-allowed'
                          : 'border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:border-blue-300 dark:hover:border-blue-600'
                      }
                    `}
                  >
                    {option.label}
                  </button>
                );
              })}
            </div>
          </div>
          
          {/* Focus Options */}
          <div className="space-y-3">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Fokus-Optionen
            </label>
            
            {/* Focus on weak questions */}
            <label className="flex items-start gap-3 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 theme-transition">
              <input
                type="checkbox"
                checked={focusWeakQuestions}
                onChange={(e) => setFocusWeakQuestions(e.target.checked)}
                className="mt-0.5 w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <div className="flex-1">
                <div className="font-medium text-gray-900 dark:text-white">
                  Schwache Fragen bevorzugen
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Fragen, die du öfter falsch beantwortet hast, werden häufiger angezeigt
                </div>
              </div>
            </label>
            
            {/* Include new questions */}
            <label className="flex items-start gap-3 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 theme-transition">
              <input
                type="checkbox"
                checked={includeNewQuestions}
                onChange={(e) => setIncludeNewQuestions(e.target.checked)}
                className="mt-0.5 w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <div className="flex-1">
                <div className="font-medium text-gray-900 dark:text-white">
                  Auch neue Fragen einbeziehen
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Fragen, die du noch nie gesehen hast, werden mit einbezogen
                </div>
              </div>
            </label>
          </div>
          
          {/* Info */}
          {availableQuestions === 0 && (
            <div className="p-3 bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-700 rounded-lg">
              <p className="text-sm text-yellow-800 dark:text-yellow-200">
                Noch keine Fragen verfügbar. Nimm zuerst an einem Quiz teil.
              </p>
            </div>
          )}
        </div>
        
        {/* Footer */}
        <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2.5 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 font-medium rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 theme-transition"
          >
            Abbrechen
          </button>
          <button
            onClick={handleStart}
            disabled={availableQuestions === 0}
            className="flex-1 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg theme-transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            Quiz starten
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
}

PracticeSettingsModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onStart: PropTypes.func.isRequired,
  courseName: PropTypes.string.isRequired,
  availableQuestions: PropTypes.number
};

export default PracticeSettingsModal;
